import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import Button from '@material-ui/core/Button';

// CONSTANTS
import { StreamingRewardsAppClaimStatus } from '@/constants/streamingRewards';
import { ROUTE_PROFILE_SUBSCRIPTION_DETAIL } from '@/constants/routes';

// UTILITY
import { getLink, routeEnhance } from '@/utility/routes';

const { CLAIMED, REGISTERED, IN_PROGRESS, UNAVAILABLE } = StreamingRewardsAppClaimStatus;

const ConciergeButton = ({ data, onClick }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { cardAvailable } = useSelector(({ auth }) => ({
    cardAvailable: !!auth.user?.card?.last4,
  }));

  const isClaimed = data?.claim_status === CLAIMED;
  const isInProgress = data?.claim_status === IN_PROGRESS;
  const isRegistered = data?.claim_status === REGISTERED;
  const isUnavailable = data?.claim_status === UNAVAILABLE;
  const isOneOfClaimed = isClaimed || isInProgress || isRegistered;
  const disabled = !cardAvailable || isUnavailable;

  const handleClick = e => {
    if (isOneOfClaimed) {
      const appLink = getLink(ROUTE_PROFILE_SUBSCRIPTION_DETAIL, {
        slug: data.unique_slug || data.slug,
      });

      navigate(routeEnhance(appLink));

      return;
    }

    onClick(e, data);
  };

  const buttonTitle = React.useMemo(() => {
    if (isClaimed) {
      return 'buttons.pending';
    }

    if (isRegistered) {
      return 'buttons.registered';
    }

    if (isInProgress) {
      return 'buttons.inProgress';
    }

    return 'buttons.select';
  }, [params?.slug, isClaimed, isInProgress, isRegistered]);

  return (
    <Button
      disabled={disabled}
      onClick={handleClick}
      color="primary"
      variant={isOneOfClaimed ? 'contained' : 'outlined'}
      fullWidth
      size="medium"
      className={clsx('app-card__btn m-t-15', {
        'app-card__btn--claimed': isOneOfClaimed,
      })}
    >
      {t(buttonTitle)}
    </Button>
  );
};

ConciergeButton.propTypes = {
  data: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default React.memo(ConciergeButton);
